import React, { useState } from "react";
import { Globe, Star, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LangCard = ({ l, active, count, busy, onToggle }) => (
  <button
    onClick={() => onToggle(l.code)} disabled={busy} data-testid={`lc-hub-card-${l.code}`}
    className={`rounded-xl border p-4 text-left transition ${
      active
        ? "border-brand-amber/50 bg-brand-amber/5 shadow-[0_0_20px_rgba(245,158,11,0.08)]"
        : "border-line bg-bg-surface hover:border-ink-muted/40"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="font-display text-2xl font-bold text-ink-primary uppercase">{l.code}</span>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-muted" /> : (
        <span className={`text-[9px] font-mono uppercase tracking-wider ${active ? "text-brand-amber" : "text-ink-muted"}`}>
          {active ? "Active" : "Off"}
        </span>
      )}
    </div>
    <div className="mt-2 text-sm font-semibold text-ink-primary">{l.name}</div>
    <div className="text-xs text-ink-muted">{l.native}</div>
    {count > 0 && <div className="mt-2 text-[10px] font-mono text-ink-secondary">{count} pieces created</div>}
  </button>
);

export const LangHubTab = ({ overview, stationId, refresh }) => {
  const [busy, setBusy] = useState("");
  if (!overview) return null;
  const { languages, active_languages: active, counts = {} } = overview;

  const toggle = async (code) => {
    const next = active.includes(code) ? active.filter((c) => c !== code) : [...active, code];
    if (!next.length) return toast.error("Keep at least one language active");
    setBusy(code);
    try {
      await api.patch("/language-center/settings", { station_id: stationId, active_languages: next });
      await refresh();
    } catch {
      toast.error("Could not update languages");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-8" data-testid="lc-hub-tab">
      <div className="rounded-2xl border border-sky-500/30 bg-gradient-to-r from-sky-500/10 to-transparent p-6">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-sky-500/20 p-3"><Globe className="h-6 w-6 text-sky-300" /></div>
          <div>
            <h2 className="font-display text-xl font-bold text-ink-primary">Global Language Center</h2>
            <p className="text-sm text-ink-secondary mt-1">
              Create content once. Broadcast in {languages.all.length} languages. Reach audiences from New York to Kingston, Paris to São Paulo.
            </p>
            <div className="mt-2 flex gap-4 text-xs font-mono">
              <span className="text-sky-300">Phase 1: {languages.launch.length + languages.phase1.length} World Languages</span>
              <span className="text-emerald-400">Phase 2: {languages.caribbean.length} Caribbean Languages</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="type-section-heading text-ink-primary">Your Active Languages</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{active.length} active</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {active.map((code) => {
            const l = languages.all.find((x) => x.code === code);
            return l ? (
              <span key={code} data-testid={`lc-active-chip-${code}`}
                className="inline-flex items-center gap-2 rounded-lg border border-brand-amber/40 bg-brand-amber/10 px-3 py-2 text-sm text-ink-primary">
                <span className="font-mono text-[9px] uppercase text-brand-amber">{l.code}</span>
                <span className="font-semibold">{l.name}</span>
              </span>
            ) : null;
          })}
          <span className="inline-flex items-center gap-1 rounded-lg border border-dashed border-line px-3 py-2 text-sm text-ink-muted">
            <Plus className="h-3.5 w-3.5" /> Toggle any card below
          </span>
        </div>
      </div>

      <div>
        <h3 className="type-section-heading text-ink-primary mb-3">Phase 1 — World Languages</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[...languages.launch, ...languages.phase1].map((l) => (
            <LangCard key={l.code} l={l} active={active.includes(l.code)} count={counts[l.code] || 0}
              busy={busy === l.code} onToggle={toggle} />
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Star className="h-4 w-4 text-emerald-400" />
          <h3 className="type-section-heading text-ink-primary">Phase 2 — Caribbean Languages</h3>
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider text-emerald-300">
            Unique Selling Point
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {languages.caribbean.map((l) => (
            <LangCard key={l.code} l={l} active={active.includes(l.code)} count={counts[l.code] || 0}
              busy={busy === l.code} onToggle={toggle} />
          ))}
        </div>
      </div>
    </div>
  );
};
